import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-token-panel.css?inline';
import { withCsrf } from '../../lib/csrf.js';
import '../pk-modal/pk-modal.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TokenRecord {
    tokenId:     string;
    createdAt:   string;
    lastUsedAt?: string | null;
    status:      string;
}

type PanelState = 'loading' | 'ok' | 'error';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso.slice(0, 16); }
}

function tokenSuffix(id: string): string {
    return '…' + id.slice(-8);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<pk-token-panel>` — API token management for a single system.
 *
 * Attributes:
 *   `system-id`  (REQUIRED) — the system to manage tokens for.
 *   `tenant-id`  (REQUIRED) — the owning tenant.
 *   `api-base`   — base path prefix (default `/portal/api/al`).
 *
 * Events:
 *   `pk-token-minted`  — detail `{ tokenId, plaintext }`.
 *   `pk-token-revoked` — detail `{ tokenId }`.
 */
export class PkTokenPanel extends HTMLElement {
    static readonly tagName = 'pk-token-panel';

    static get observedAttributes(): string[] {
        return ['system-id', 'tenant-id', 'api-base'];
    }

    private readonly _root: ShadowRoot;

    private _tokens: TokenRecord[] = [];
    private _state: PanelState    = 'loading';
    private _errorMsg: string      = '';
    private _minting: boolean      = false;
    private _newToken: string | null = null; // plaintext of just-minted token
    private readonly _revoking = new Set<string>(); // tokenIds currently being revoked

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    get systemId(): string  { return this.getAttribute('system-id') ?? ''; }
    get tenantId(): string  { return this.getAttribute('tenant-id') ?? ''; }
    get apiBase(): string   { return this.getAttribute('api-base') ?? '/portal/api/al'; }

    connectedCallback(): void {
        this._fetchTokens();
    }

    attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
        // attributeChangedCallback fires DURING HTML parse for every initial
        // attribute, before connectedCallback. Skip those — connectedCallback
        // owns the initial fetch. Only refetch on actual post-connection changes.
        if (oldVal !== newVal && this.isConnected) {
            this._fetchTokens();
        }
    }

    // ── Network ──────────────────────────────────────────────────────────────

    private async _fetchTokens(): Promise<void> {
        if (!this.systemId) return;
        this._state = 'loading';
        this._doRender();

        try {
            const resp = await fetch(
                `${this.apiBase}/systems/${encodeURIComponent(this.systemId)}/tokens`,
                withCsrf()
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json() as { items?: TokenRecord[] } | TokenRecord[];
            this._tokens = Array.isArray(data) ? data : (data.items ?? []);
            this._state  = 'ok';
        } catch (err) {
            this._state    = 'error';
            this._errorMsg = err instanceof Error ? err.message : String(err);
        }
        this._doRender();
    }

    private async _mint(): Promise<void> {
        if (this._minting) return;
        this._minting = true;
        this._doRender();

        try {
            const resp = await fetch(
                `${this.apiBase}/systems/${encodeURIComponent(this.systemId)}/tokens`,
                withCsrf({
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                })
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const created = await resp.json() as { tokenId: string; plaintext?: string; token?: string };
            const plaintext = created.plaintext ?? created.token ?? '';
            this._newToken  = plaintext;
            // Refresh the list
            await this._fetchTokens();
            // Open the reveal modal
            this._doRender();
            const modal = this._root.querySelector('pk-modal') as HTMLElement & { show(): void };
            modal?.show?.();

            this.dispatchEvent(new CustomEvent('pk-token-minted', {
                bubbles: true,
                composed: true,
                detail: { tokenId: created.tokenId, plaintext },
            }));
        } catch (err) {
            this._errorMsg = err instanceof Error ? err.message : String(err);
        } finally {
            this._minting = false;
            this._doRender();
        }
    }

    private async _revoke(tokenId: string): Promise<void> {
        if (this._revoking.has(tokenId)) return;
        if (!confirm(`Revoke token ${tokenSuffix(tokenId)}? This cannot be undone.`)) return;

        this._revoking.add(tokenId);
        this._doRender();

        try {
            const resp = await fetch(
                `${this.apiBase}/systems/${encodeURIComponent(this.systemId)}/tokens/${encodeURIComponent(tokenId)}`,
                withCsrf({ method: 'DELETE' })
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            this._tokens = this._tokens.filter(t => t.tokenId !== tokenId);
            this.dispatchEvent(new CustomEvent('pk-token-revoked', {
                bubbles: true, composed: true, detail: { tokenId },
            }));
        } catch (err) {
            this._errorMsg = err instanceof Error ? err.message : String(err);
        } finally {
            this._revoking.delete(tokenId);
            this._doRender();
        }
    }

    private async _copyToken(): Promise<void> {
        if (!this._newToken) return;
        await navigator.clipboard.writeText(this._newToken);
    }

    private _closeReveal(): void {
        this._newToken = null;
        this._doRender();
    }

    // ── Templates ──────────────────────────────────────────────────────────

    private _tableBodyTpl(): TemplateResult {
        if (this._state === 'loading') {
            return html`
                <tr class="state-row">
                    <td colspan="5" aria-busy="true">Loading tokens…</td>
                </tr>
            `;
        }
        if (this._state === 'error') {
            return html`
                <tr class="error-row state-row">
                    <td colspan="5" role="alert">${this._errorMsg}</td>
                </tr>
            `;
        }
        if (this._tokens.length === 0) {
            return html`
                <tr class="state-row">
                    <td colspan="5">No tokens yet — mint one to get started.</td>
                </tr>
            `;
        }
        return html`${this._tokens.map(tok => this._tokenRowTpl(tok))}`;
    }

    private _tokenRowTpl(tok: TokenRecord): TemplateResult {
        const suffix = tokenSuffix(tok.tokenId);
        const isActive = tok.status.toLowerCase() === 'active';
        const badgeClass = isActive ? 'badge-active' : 'badge-revoked';
        const isRevoking = this._revoking.has(tok.tokenId);
        const revokeLabel = `Revoke token ${suffix}`;
        const revokeText = isRevoking ? 'Revoking…' : 'Revoke';
        return html`
            <tr>
                <td><span class="token-id">${suffix}</span></td>
                <td>${fmtDate(tok.createdAt)}</td>
                <td>${fmtDate(tok.lastUsedAt)}</td>
                <td>
                    <span class="badge ${badgeClass}">
                        ${tok.status}
                    </span>
                </td>
                <td>
                    ${isActive ? html`
                        <button class="revoke-btn"
                            type="button"
                            aria-label=${revokeLabel}
                            ?disabled=${isRevoking}
                            @click=${() => this._revoke(tok.tokenId)}>
                            ${revokeText}
                        </button>
                    ` : nothing}
                </td>
            </tr>
        `;
    }

    private template(): TemplateResult {
        return html`
            <style>${styles}</style>

            <div class="panel">
                <div class="header">
                    <h2 class="header-title">Tokens for ${this.systemId || '—'}</h2>
                    <button class="mint-btn"
                        type="button"
                        ?disabled=${this._minting}
                        aria-busy=${this._minting ? 'true' : 'false'}
                        @click=${() => this._mint()}>
                        ${this._minting ? 'Minting…' : 'Mint new token'}
                    </button>
                </div>

                ${this._errorMsg && this._state !== 'error' ? html`
                    <div class="alert" role="alert">${this._errorMsg}</div>
                ` : nothing}

                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Token ID</th>
                                <th>Created</th>
                                <th>Last Used</th>
                                <th>Status</th>
                                <th><span class="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._tableBodyTpl()}
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Reveal-once modal -->
            <pk-modal title="Your new token"
                size="large"
                @pk-cancel=${() => this._closeReveal()}>
                ${this._newToken ? html`
                    <div class="mint-reveal">
                        <p>Copy your token now — we will not show it again.</p>
                        <div class="copy-row">
                            <input class="token-input"
                                type="text"
                                readonly
                                aria-label="New token value"
                                .value=${this._newToken} />
                            <button class="copy-btn"
                                type="button"
                                @click=${() => this._copyToken()}>
                                Copy
                            </button>
                        </div>
                    </div>
                ` : nothing}
                <button slot="primary"
                    type="button"
                    style="padding:0.4rem 1rem;border:none;border-radius:6px;
                           background:var(--pk-color-primary,#0E7C7B);
                           color:var(--pk-color-primary-ink,#fff);
                           font-family:inherit;font-size:0.85rem;cursor:pointer;"
                    @click=${() => {
                        const modal = this._root.querySelector('pk-modal') as HTMLElement & { hide(): void };
                        modal?.hide?.();
                    }}>
                    Done — I have saved this
                </button>
            </pk-modal>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTokenPanel.tagName)) {
    customElements.define(PkTokenPanel.tagName, PkTokenPanel);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-token-panel': PkTokenPanel;
    }
}
